import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../../config';
import './embed.css';

// The broadcast URL (gets long duration — the "TV thing")
const BROADCAST_URL = 'https://komokshow.netlify.app/simulate/?broadcast=true&displayMode=video-composite';

// Pool of quick-rotate pages — each can override duration (ms)
// Prioritise multi-stock / overview pages for big TV readability
const QUICK_POOL = [
  { url: '/monitor/index.html' },                            // full monitor dashboard
  { url: '/embed/dashboard' },                                // market grid + leaderboard
  { url: '/embed/dashboard?chart=false' },                    // market grid + leaderboard (no charts)
  { url: '/embed/leaderboard-logo' },                         // leaderboard with logo
  { url: '/embed/leaderboard?limit=50&style=compact' },       // compact leaderboard
  { url: '/embed/market?chart=true' },                        // all stocks with charts
  { url: '/embed/market?chart=false' },                       // all stocks no charts
  { url: '/embed/random-stocks' },                            // random stocks grid
  { url: '/embed/trades-table' },                             // recent trades
  { url: '/embed/events-table' },                             // events table
  { url: '/embed/recent-trades' },                            // trades ticker
  { url: '/embed/random-detail?variant=full', duration: 30000 }, // single stock full detail — 30s only
];

// Pages only useful when market is NOT open
const MARKET_CLOSED_ONLY = [
  { url: '/embed/countdown' },
];

function pickRandom(pool, excludeUrl) {
  const filtered = pool.filter(p => p.url !== excludeUrl);
  if (filtered.length === 0) return pool[0];
  return filtered[Math.floor(Math.random() * filtered.length)];
}

const EmbedHome = () => {
  const [searchParams] = useSearchParams();

  const broadcastDuration = parseInt(searchParams.get('broadcast')) || 300000; // 5 min
  const quickDuration = parseInt(searchParams.get('quick')) || 60000; // 1 min
  const showLogo = searchParams.get('logo') !== 'false';

  const [phase, setPhase] = useState('not_started');
  const [currentPage, setCurrentPage] = useState(null); // { url, duration? } or broadcast URL string
  const [isBroadcast, setIsBroadcast] = useState(false);
  const [pageDuration, setPageDuration] = useState(null); // per-page override
  const [key, setKey] = useState(0);
  const socketRef = useRef(null);
  const quickCountRef = useRef(0);
  // ~5 quick pages (60s each) = ~5 min between broadcasts
  const quickBetweenBroadcasts = useRef(4 + Math.floor(Math.random() * 2)); // 4-5 quick pages

  const isMarketOpen = phase === 'trading' || phase === 'premarket';

  // Build the active pool based on market state
  const getPool = useCallback(() => {
    if (isMarketOpen) return QUICK_POOL;
    return [...QUICK_POOL, ...MARKET_CLOSED_ONLY];
  }, [isMarketOpen]);

  // Pick the next page
  const nextPage = useCallback(() => {
    const count = quickCountRef.current;
    const target = quickBetweenBroadcasts.current;

    if (count >= target) {
      // Time for broadcast
      quickCountRef.current = 0;
      quickBetweenBroadcasts.current = 4 + Math.floor(Math.random() * 2);
      setIsBroadcast(true);
      setPageDuration(null);
      setCurrentPage(BROADCAST_URL);
    } else {
      // Pick a random quick page
      quickCountRef.current = count + 1;
      setIsBroadcast(false);
      setCurrentPage(prev => {
        const prevUrl = typeof prev === 'string' ? prev : prev?.url;
        const picked = pickRandom(getPool(), prevUrl);
        setPageDuration(picked.duration || null);
        return picked.url;
      });
    }
    setKey(k => k + 1);
  }, [getPool]);

  // Fetch party phase
  useEffect(() => {
    const fetchState = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/party-state`);
        if (res.ok) {
          const data = await res.json();
          if (data.partyState) setPhase(data.partyState.phase || 'not_started');
        }
      } catch (e) { console.error(e); }
    };

    fetchState();

    socketRef.current = io(API_BASE_URL, { auth: { public: true } });
    socketRef.current.on('party-state-update', (data) => {
      if (data.partyState) setPhase(data.partyState.phase || 'not_started');
    });
    socketRef.current.on('registration-start', () => setPhase('registration'));
    socketRef.current.on('premarket-start', () => setPhase('premarket'));
    socketRef.current.on('market-bell', (data) => {
      if (data.type === 'OPENING_BELL') setPhase('trading');
      else if (data.type === 'CLOSING_BELL') setPhase('closing');
    });
    socketRef.current.on('party-restart', () => setPhase('not_started'));

    const pollInterval = setInterval(fetchState, 30000);
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      clearInterval(pollInterval);
    };
  }, []);

  // Start first page
  useEffect(() => {
    if (!currentPage) nextPage();
  }, [currentPage, nextPage]);

  // Rotate on timer
  useEffect(() => {
    if (!currentPage) return;
    const duration = isBroadcast ? broadcastDuration : (pageDuration || quickDuration);
    const timer = setTimeout(nextPage, duration);
    return () => clearTimeout(timer);
  }, [key, isBroadcast, broadcastDuration, quickDuration, pageDuration, nextPage]);

  if (!currentPage) return null;

  return (
    <div className="embed-home">
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>

      <iframe
        key={key}
        src={currentPage}
        title="Home Display"
        className="embed-home-iframe"
        allowFullScreen
      />

      {/* Persistent Komok logo watermark */}
      {showLogo && !currentPage.includes('leaderboard-logo') && (
        <div className="embed-home-logo">
          <img src="/komok_logo.webp" alt="KOMOK" />
        </div>
      )}
    </div>
  );
};

export default EmbedHome;
